// src/popup/MultiDatePopup.js
import React, { useMemo, useState } from "react";
import { useAuth } from "../contexts/AuthContext";

// Popup-komponent för att välja flera datum för pass
export default function MultiDatePopup({ open, onClose, onSave }) {
  const { currentUser, profile, companyAccountUid } = useAuth();

  // State för aktiv flik
  const [activeTab, setActiveTab] = useState("weekday");
  
  // State för enstaka datum
  const [singleDate, setSingleDate] = useState("");
  const [selectedDates, setSelectedDates] = useState([]);
  
  // State för veckodagsintervall
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [weekdays, setWeekdays] = useState({
    mon: false,
    tue: false,
    wed: false,
    thu: false,
    fri: false,
    sat: false,
    sun: false,
  });

  // Hämta företagskontots UID (använder companyAccountUid om det finns)
  const accountToUse = companyAccountUid || currentUser?.uid;

  // Mappning av veckodagar till siffror (0-6)
  const weekdayDateMap = {
    sun: 0,
    mon: 1,
    tue: 2,
    wed: 3,
    thu: 4,
    fri: 5,
    sat: 6,
  };

  // Automatiskt genererade datum baserat på valda veckodagar och intervall
  const autoSelectedWeekdayDates = useMemo(() => {
    if (!startDate || !endDate) return [];

    const start = new Date(startDate);
    const end = new Date(endDate);

    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      return [];
    }

    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);

    if (start > end) return [];

    const activeWeekdays = Object.keys(weekdays).filter((key) => weekdays[key]);
    if (activeWeekdays.length === 0) return [];

    const activeDayNumbers = activeWeekdays.map((key) => weekdayDateMap[key]);
    const result = [];

    const current = new Date(start);

    while (current <= end) {
      if (activeDayNumbers.includes(current.getDay())) {
        const yyyy = current.getFullYear();
        const mm = String(current.getMonth() + 1).padStart(2, "0");
        const dd = String(current.getDate()).padStart(2, "0");
        result.push(`${yyyy}-${mm}-${dd}`);
      }

      current.setDate(current.getDate() + 1);
    }

    return result;
  }, [startDate, endDate, weekdays]);

  // Lägg till ett enstaka datum
  const addDate = () => {
    if (!singleDate) return;
    if (selectedDates.includes(singleDate)) return;

    setSelectedDates((prev) => [...prev, singleDate].sort());
    setSingleDate("");
  };

  // Ta bort ett datum från listan
  const removeDate = (dateToRemove) => {
    setSelectedDates((prev) => prev.filter((date) => date !== dateToRemove));
  };

  // Hantera checkbox-ändring för veckodagar
  const handleCheckboxChange = (key) => {
    setWeekdays((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  // Spara valda datum
  const handleSave = () => {
    const payload = {
      mode: activeTab,
      dates: activeTab === "weekday" ? autoSelectedWeekdayDates : selectedDates,
      startDate,
      endDate,
      weekdays,
    };

    if (onSave) onSave(payload);
    if (onClose) onClose();
  };

  // Stäng popup om den inte är öppen
  if (!open) return null;

  // Stilar för komponenten
  const styles = {
    overlay: {
      position: "fixed",
      inset: 0,
      background: "rgba(0, 0, 0, 0.22)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 9999,
      padding: 12,
    },

    modal: {
      width: "100%",
      maxWidth: "680px",
      background: "#f3f3f3",
      borderRadius: 14,
      padding: "18px 20px",
      boxSizing: "border-box",
      maxHeight: "82vh",
      overflowY: "auto",
    },

    title: {
      margin: 0,
      fontSize: 20,
      fontWeight: 700,
      color: "#111827",
    },

    companyInfo: {
      marginTop: 4,
      fontSize: 12,
      color: "#8b8fa1",
    },

    divider: {
      height: 1,
      background: "#d6d6d6",
      margin: "12px 0 16px",
    },

    tabRow: {
      display: "flex",
      gap: 10,
      marginBottom: 16,
    },

    tabButton: {
      flex: 1,
      height: 42,
      borderRadius: 10,
      border: "none",
      fontSize: 14,
      fontWeight: 500,
      cursor: "pointer",
      transition: "all 0.2s",
    },

    tabButtonActive: {
      background: "#2f66e8",
      color: "#fff",
    },

    tabButtonInactive: {
      background: "#dddddf",
      color: "#111827",
    },

    inlineRow: {
      display: "flex",
      gap: 10,
      alignItems: "center",
      marginBottom: 16,
    },

    fieldBlock: {
      marginBottom: 14,
    },

    fieldLabel: {
      display: "block",
      fontSize: 14,
      fontWeight: 500,
      marginBottom: 6,
      color: "#111827",
    },

    input: {
      width: "100%",
      height: 42,
      borderRadius: 10,
      border: "1px solid #d8dbe0",
      padding: "0 12px",
      fontSize: 14,
      outline: "none",
      boxSizing: "border-box",
      background: "#fff",
      color: "#111827",
      transition: "border-color 0.2s",
    },

    addButton: {
      width: 90,
      height: 42,
      border: "none",
      borderRadius: 10,
      background: "#2f66e8",
      color: "#fff",
      fontSize: 14,
      fontWeight: 600,
      cursor: "pointer",
      transition: "opacity 0.2s",
    },

    checkboxRow: {
      display: "flex",
      flexWrap: "wrap",
      gap: 12,
      alignItems: "center",
    },

    checkboxLabel: {
      display: "flex",
      alignItems: "center",
      gap: 6,
      fontSize: 13,
      color: "#111827",
      cursor: "pointer",
    },

    section: {
      marginTop: 10,
    },

    label: {
      fontSize: 14,
      fontWeight: 500,
      color: "#111827",
      marginBottom: 8,
    },

    emptyText: {
      fontSize: 13,
      color: "#8b8fa1",
      fontStyle: "italic",
    },

    selectedDateTags: {
      display: "flex",
      flexWrap: "wrap",
      gap: 10,
      marginTop: 8,
    },

    dateChip: {
      background: "#55a8e8",
      color: "#fff",
      padding: "10px 16px",
      borderRadius: 10,
      fontSize: 14,
      fontWeight: 600,
    },

    dateTag: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      background: "#55a8e8",
      color: "#fff",
      padding: "10px 14px",
      borderRadius: 10,
      fontSize: 14,
      fontWeight: 600,
    },

    tagRemoveBtn: {
      border: "none",
      background: "transparent",
      color: "#fff",
      fontSize: 18,
      cursor: "pointer",
      lineHeight: 1,
      padding: 0,
      transition: "opacity 0.2s",
    },

    footer: {
      display: "flex",
      justifyContent: "flex-end",
      gap: 10,
      marginTop: 18,
    },

    cancelButton: {
      minWidth: 90,
      height: 40,
      border: "none",
      borderRadius: 10,
      background: "#dddddf",
      color: "#111827",
      fontSize: 14,
      fontWeight: 500,
      cursor: "pointer",
      transition: "opacity 0.2s",
    },

    saveButton: {
      minWidth: 130,
      height: 40,
      border: "none",
      borderRadius: 10,
      background: "#2f66e8",
      color: "#fff",
      fontSize: 14,
      fontWeight: 600,
      cursor: "pointer",
      transition: "opacity 0.2s",
    },
  };

  // Hover-effekt för knappar
  const handleButtonHover = (e, isHover) => {
    if (isHover) {
      e.currentTarget.style.opacity = "0.85";
    } else {
      e.currentTarget.style.opacity = "1";
    }
  };

  // Fokus-effekt för input-fält
  const handleInputFocus = (e) => {
    e.target.style.borderColor = "#2f66e8";
  };

  const handleInputBlur = (e) => {
    e.target.style.borderColor = "#d8dbe0";
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Rubrik */}
        <h2 style={styles.title}>Välj flera dagar för pass</h2>
        
        {/* Företagsinformation */}
        {accountToUse && (
          <div style={styles.companyInfo}>
            Företags-ID: {accountToUse.substring(0, 8)}...
          </div>
        )}
        
        <div style={styles.divider} />

        {/* Flikar */}
        <div style={styles.tabRow}>
          <button
            type="button"
            onClick={() => setActiveTab("weekday")}
            style={{
              ...styles.tabButton,
              ...(activeTab === "weekday"
                ? styles.tabButtonActive
                : styles.tabButtonInactive),
            }}
            onMouseEnter={(e) => handleButtonHover(e, true)}
            onMouseLeave={(e) => handleButtonHover(e, false)}
          >
            Veckodagar
          </button>

          <button
            type="button"
            onClick={() => setActiveTab("date")}
            style={{
              ...styles.tabButton,
              ...(activeTab === "date"
                ? styles.tabButtonActive
                : styles.tabButtonInactive),
            }}
            onMouseEnter={(e) => handleButtonHover(e, true)}
            onMouseLeave={(e) => handleButtonHover(e, false)}
          >
            Välj datum
          </button>
        </div>

        {/* Flik: Välj datum */}
        {activeTab === "date" && (
          <>
            <div style={styles.inlineRow}>
              <div style={{ flex: 1 }}>
                <input
                  type="date"
                  value={singleDate}
                  onChange={(e) => setSingleDate(e.target.value)}
                  style={styles.input}
                  onFocus={handleInputFocus}
                  onBlur={handleInputBlur}
                />
              </div>

              <button
                type="button"
                onClick={addDate}
                style={styles.addButton}
                onMouseEnter={(e) => handleButtonHover(e, true)}
                onMouseLeave={(e) => handleButtonHover(e, false)}
              >
                Lägg till
              </button>
            </div>

            <div style={styles.section}>
              <div style={styles.label}>Valda datum</div>

              {selectedDates.length === 0 ? (
                <div style={styles.emptyText}>Inga datum tillagda...</div>
              ) : (
                <div style={styles.selectedDateTags}>
                  {selectedDates.map((date) => (
                    <div key={date} style={styles.dateTag}>
                      <span>{date}</span>
                      <button
                        type="button"
                        onClick={() => removeDate(date)}
                        style={styles.tagRemoveBtn}
                        onMouseEnter={(e) => handleButtonHover(e, true)}
                        onMouseLeave={(e) => handleButtonHover(e, false)}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {/* Flik: Veckodagar */}
        {activeTab === "weekday" && (
          <>
            <div style={styles.fieldBlock}>
              <label style={styles.fieldLabel}>Startdatum</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                style={styles.input}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
              />
            </div>

            <div style={styles.fieldBlock}>
              <label style={styles.fieldLabel}>Slutdatum</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                style={styles.input}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
              />
            </div>

            <div style={styles.fieldBlock}>
              <div style={styles.fieldLabel}>Välj veckodagar</div>

              <div style={styles.checkboxRow}>
                <label style={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={weekdays.mon}
                    onChange={() => handleCheckboxChange("mon")}
                  />
                  <span>Mån</span>
                </label>

                <label style={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={weekdays.tue}
                    onChange={() => handleCheckboxChange("tue")}
                  />
                  <span>Tis</span>
                </label>

                <label style={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={weekdays.wed}
                    onChange={() => handleCheckboxChange("wed")}
                  />
                  <span>Ons</span>
                </label>

                <label style={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={weekdays.thu}
                    onChange={() => handleCheckboxChange("thu")}
                  />
                  <span>Tor</span>
                </label>

                <label style={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={weekdays.fri}
                    onChange={() => handleCheckboxChange("fri")}
                  />
                  <span>Fre</span>
                </label>

                <label style={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={weekdays.sat}
                    onChange={() => handleCheckboxChange("sat")}
                  />
                  <span>Lör</span>
                </label>

                <label style={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={weekdays.sun}
                    onChange={() => handleCheckboxChange("sun")}
                  />
                  <span>Sön</span>
                </label>
              </div>
            </div>

            <div style={styles.section}>
              <div style={styles.label}>Valda datum</div>

              {autoSelectedWeekdayDates.length === 0 ? (
                <div style={styles.emptyText}>
                  {startDate && endDate
                    ? "Inga datum matchar valda veckodagar..."
                    : "Välj start- och slutdatum för att se datum..."}
                </div>
              ) : (
                <div style={styles.selectedDateTags}>
                  {autoSelectedWeekdayDates.map((date) => (
                    <div key={date} style={styles.dateChip}>
                      {date}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {/* Knappar */}
        <div style={styles.footer}>
          <button
            type="button"
            onClick={onClose}
            style={styles.cancelButton}
            onMouseEnter={(e) => handleButtonHover(e, true)}
            onMouseLeave={(e) => handleButtonHover(e, false)}
          >
            Avbryt
          </button>
          <button
            type="button"
            onClick={handleSave}
            style={styles.saveButton}
            onMouseEnter={(e) => handleButtonHover(e, true)}
            onMouseLeave={(e) => handleButtonHover(e, false)}
          >
            Lägg till pass
          </button>
        </div>
      </div>
    </div>
  );
}